import React, { useEffect, useState } from "react";
import { Button, Input, Row, Col, Form, Typography } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./index.css";
import RemitClickInfo from "../Components/RemitClickInfo";
import AppActionModal from "../Components/AppActionModal";
import { CheckCircleFilled, CloseOutlined } from "@ant-design/icons";
import logo from "../Image/logo.png";
import { confirmResetPassword, resetPassword, signIn } from "aws-amplify/auth";

const RESEND_OTP_SECONDS = 60;
const DASHBOARD_VERIFY_PROMPT_TRIGGER_KEY =
  "dashboardVerificationPromptShouldShow";

const normalizeResetPasswordError = (error) => {
  const rawMessage =
    error?.message || error?.toString?.().split(":").pop()?.trim() || "";

  if (
    rawMessage === "Username/client id combination not found." ||
    rawMessage === "User does not exist."
  ) {
    return "We couldn't find an account with that email address.";
  }

  if (
    rawMessage.includes("Exceeded daily email limit") ||
    rawMessage.includes("LimitExceededException")
  ) {
    return "Too many attempts. Please try again later.";
  }

  if (
    rawMessage.includes("Invalid verification code") ||
    rawMessage.includes("CodeMismatchException")
  ) {
    return "The verification code is incorrect.";
  }

  if (
    rawMessage.includes("ExpiredCodeException") ||
    rawMessage.includes("Invalid code provided")
  ) {
    return "The verification code has expired. Please request a new code.";
  }

  if (rawMessage.includes("Invalid password")) {
    return "Please choose a stronger password.";
  }

  return rawMessage || "Unable to complete your request right now.";
};

const ForgetPassword = () => {
  const { Text } = Typography;
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const isMobile = viewportWidth < 768;
  const [email, setEmail] = useState("");
  const [isConfirmStage, setIsConfirmStage] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isSubmittingReset, setIsSubmittingReset] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successDestination, setSuccessDestination] = useState("signIn");
  const passwordValue = Form.useWatch("password", form);
  const confirmPasswordValue = Form.useWatch("confirmPassword", form);
  const isConfirmPasswordMatched =
    Boolean(confirmPasswordValue) && passwordValue === confirmPasswordValue;

  const presetEmail = String(location.state?.email || "")
    .trim()
    .toLowerCase();
  const presetMessage = String(location.state?.message || "").trim();
  const shouldAutoSendResetCode = Boolean(location.state?.sendResetCode);
  const shouldAutoSignInAfterReset = Boolean(
    location.state?.autoSignInAfterReset,
  );

  useEffect(() => {
    const handleWindowResize = () => {
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    const timer = window.setTimeout(() => {
      setResendCountdown((current) => current - 1);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [resendCountdown]);

  useEffect(() => {
    if (!presetEmail) {
      return;
    }

    form.setFieldsValue({ email: presetEmail });
    setEmail(presetEmail);

    if (presetMessage) {
      setInfoMessage(presetMessage);
    }
  }, [form, presetEmail, presetMessage]);

  useEffect(() => {
    if (!presetEmail || !shouldAutoSendResetCode || isConfirmStage) {
      return;
    }

    let isCancelled = false;

    const sendCode = async () => {
      try {
        setIsSendingCode(true);
        await requestResetCode(presetEmail);
        if (!isCancelled && presetMessage) {
          setInfoMessage(presetMessage);
        }
      } catch (error) {
        if (!isCancelled) {
          setErrorMessage(normalizeResetPasswordError(error));
          setInfoMessage("");
        }
      } finally {
        if (!isCancelled) {
          setIsSendingCode(false);
        }
      }
    };

    sendCode();

    return () => {
      isCancelled = true;
    };
  }, [isConfirmStage, presetEmail, presetMessage, shouldAutoSendResetCode]);

  const requestResetCode = async (username) => {
    const response = await resetPassword({ username });
    if (
      response?.nextStep?.resetPasswordStep ===
      "CONFIRM_RESET_PASSWORD_WITH_CODE"
    ) {
      setIsConfirmStage(true);
      setResendCountdown(RESEND_OTP_SECONDS);
      setInfoMessage(`We sent a verification code to ${username}.`);
      setErrorMessage("");
      return;
    }

    setInfoMessage("Password reset is already complete.");
  };

  const handleRequestReset = async () => {
    try {
      const values = await form.validateFields(["email"]);
      const nextEmail = String(values.email || "").trim().toLowerCase();
      setIsSendingCode(true);
      setErrorMessage("");
      setEmail(nextEmail);
      await requestResetCode(nextEmail);
    } catch (error) {
      if (!error?.errorFields) {
        setErrorMessage(normalizeResetPasswordError(error));
      } else {
        setErrorMessage("");
      }
      setInfoMessage("");
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleConfirmReset = async () => {
    try {
      const values = await form.validateFields([
        "otp",
        "password",
        "confirmPassword",
      ]);

      setIsSubmittingReset(true);
      setErrorMessage("");
      await confirmResetPassword({
        username: email,
        confirmationCode: String(values.otp || "").trim(),
        newPassword: values.password,
      });

      if (shouldAutoSignInAfterReset) {
        try {
          await signIn({
            username: email,
            password: values.password,
          });
          localStorage.setItem(
            DASHBOARD_VERIFY_PROMPT_TRIGGER_KEY,
            "true",
          );
          localStorage.setItem("loginTimestamp", new Date().getTime().toString());
          setSuccessDestination("dashboard");
        } catch (signInError) {
          console.error("Auto sign-in after password reset failed:", signInError);
          setSuccessDestination("signIn");
        }
      } else {
        setSuccessDestination("signIn");
      }

      setErrorMessage("");
      setInfoMessage("");
      setShowSuccessModal(true);
    } catch (error) {
      if (!error?.errorFields) {
        setErrorMessage(normalizeResetPasswordError(error));
      } else {
        setErrorMessage("");
      }
      setInfoMessage("");
    } finally {
      setIsSubmittingReset(false);
    }
  };

  const handleResendOtp = async () => {
    if (!email || resendCountdown > 0 || isSendingCode || isSubmittingReset) {
      return;
    }

    try {
      setIsSendingCode(true);
      await requestResetCode(email);
      setInfoMessage(`A new verification code was sent to ${email}.`);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(normalizeResetPasswordError(error));
      setInfoMessage("");
    } finally {
      setIsSendingCode(false);
    }
  };

  const renderEmailStage = () => (
    <>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        validateTrigger="onSubmit"
      >
        <label className="formlabel" style={{ fontSize: "16px" }}>
          Email
        </label>
        <Form.Item
          name="email"
          rules={[
            { required: true, message: "Please enter your email address." },
            { type: "email", message: "Please enter a valid email address." },
          ]}
        >
          <Input
            className="forminput"
            placeholder="Email"
            autoComplete="email"
          />
        </Form.Item>

        {errorMessage ? (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "1rem" }}>
            <Text style={{ color: "red" }}>{errorMessage}</Text>
          </div>
        ) : null}

        <Button
          className="signupbtn"
          onClick={handleRequestReset}
          loading={isSendingCode}
          disabled={isSendingCode || isSubmittingReset}
        >
          Send Reset Code
        </Button>
      </Form>
      <p className="signupFooter">
        Remember your password?{" "}
        <Link to="/signIn" style={{ textDecoration: "none" }}>
          Sign In
        </Link>
      </p>
    </>
  );

  const renderConfirmStage = () => (
    <>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        validateTrigger="onSubmit"
      >
        {infoMessage ? (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "0.75rem" }}>
            <Text style={{ color: "#325FD5" }}>{infoMessage}</Text>
          </div>
        ) : null}

        <label className="formlabel" style={{ fontSize: "16px" }}>
          Verification Code
        </label>
        <Form.Item
          name="otp"
          rules={[
            { required: true, message: "Please enter the verification code." },
            { len: 6, message: "Please enter the 6-digit verification code." },
          ]}
        >
          <Input
            className="forminput"
            placeholder="6-digit code"
            maxLength={6}
            autoComplete="one-time-code"
          />
        </Form.Item>

        <label className="formlabel" style={{ fontSize: "16px" }}>
          New Password
        </label>
        <Form.Item
          name="password"
          rules={[
            { required: true, message: "Please enter your new password." },
            { min: 8, message: "Password must be at least 8 characters." },
          ]}
        >
          <Input.Password
            className="forminput"
            placeholder="Enter new password"
            autoComplete="new-password"
          />
        </Form.Item>

        <label className="formlabel" style={{ fontSize: "16px" }}>
          Confirm Password
        </label>
        <Form.Item
          name="confirmPassword"
          dependencies={["password"]}
          hasFeedback={isConfirmPasswordMatched}
          rules={[
            { required: true, message: "Please confirm your new password." },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("The passwords you entered do not match."),
                );
              },
            }),
          ]}
        >
          <Input.Password
            className="forminput"
            placeholder="Confirm new password"
            autoComplete="new-password"
          />
        </Form.Item>

        {errorMessage ? (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "0.75rem" }}>
            <Text style={{ color: "red" }}>{errorMessage}</Text>
          </div>
        ) : null}

        <Button
          className="signupbtn"
          onClick={handleConfirmReset}
          loading={isSubmittingReset}
          disabled={isSubmittingReset}
        >
          Reset Password
        </Button>

        <Button
          type="link"
          onClick={handleResendOtp}
          disabled={resendCountdown > 0 || isSendingCode || isSubmittingReset}
          style={{ width: "100%", marginTop: "0.75rem" }}
        >
          {resendCountdown > 0
            ? `Resend code in ${resendCountdown}s`
            : "Resend Code"}
        </Button>
      </Form>
      <p className="signupFooter">
        Remember your password?{" "}
        <Link to="/signIn" style={{ textDecoration: "none" }}>
          Sign In
        </Link>
      </p>
    </>
  );

  return (
    <>
      {isMobile ? (
        <div className="signin-mobile-wrap">
          <Row
            className="signin-mobile-header"
            style={{
              display: "flex",
              borderBottomStyle: "solid",
              borderBottomColor: "#F2F2F2",
              borderBottomWidth: "0.1px",
              flexShrink: 0,
            }}
          >
            <Col
              span={20}
              style={{
                display: "flex",
                paddingTop: 10,
                paddingBottom: 10,
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", paddingLeft: "1rem" }}>
                <img src={logo} alt="BajCan" className="headerLogo headerLogo--mobile" />
              </div>
            </Col>
            <Col
              span={4}
              style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <Link to="/">
                <Button type="primary" icon={<CloseOutlined />} className="crossSign-mobile" />
              </Link>
            </Col>
          </Row>
          <Row
            className="signin-row--mobile"
            style={{ flex: 1, minHeight: 0, height: "auto" }}
          >
            <Col xs={{ span: 24 }}>
              <div className="signinRightSec">
                <div className="signin-mobile-welcome">
                  <p className="signin-mobile-tagline">
                    {isConfirmStage ? "Reset your password" : "Forgot your password?"}
                  </p>
                  <p className="signin-mobile-subtitle">
                    {isConfirmStage
                      ? "Enter the code from your email and choose a new password"
                      : "We will send a verification code to your email"}
                  </p>
                </div>
                <div className="signbox signbox--mobile-signin">
                  <h2 className="signupTitle">
                    {isConfirmStage ? "Reset Password" : "Forgot Password"}
                  </h2>
                  <div className="form">
                    {isConfirmStage ? renderConfirmStage() : renderEmailStage()}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      ) : (
        <Row style={{ height: "100vh" }}>
          <Col xs={{ span: 0 }} md={{ span: 12 }} lg={{ span: 10 }}>
            <RemitClickInfo />
          </Col>
          <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 14 }}>
            <div className="signinRightSec">
              <div className="signbox">
                <Link
                  to="/"
                  style={{ display: "flex", width: "100%", justifyContent: "flex-end" }}
                >
                  <Button type="primary" icon={<CloseOutlined />} className="crossSign" />
                </Link>
                <h2 className="signupTitle">
                  {isConfirmStage ? "Reset Password" : "Forgot Password"}
                </h2>
                <div className="form">
                  {isConfirmStage ? renderConfirmStage() : renderEmailStage()}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      )}
      <AppActionModal
        open={showSuccessModal}
        className="overviewResultModal"
        variant="success"
        icon={<CheckCircleFilled />}
        title="Password Updated"
        description={
          successDestination === "dashboard"
            ? "Your password has been reset successfully. Continue to your dashboard."
            : "Your password has been reset successfully."
        }
        closable={false}
        maskClosable={false}
        primaryAction={{
          label:
            successDestination === "dashboard"
              ? "Go to Dashboard"
              : "Back to Sign In",
          onClick: () => {
            if (successDestination === "dashboard") {
              window.location.assign("/dashboard");
              return;
            }

            navigate("/signIn", {
              state: {
                passwordResetSuccess: true,
                prefillUsername: email,
              },
            });
          },
          className: "overviewResultBtn",
        }}
      />
    </>
  );
};

export default ForgetPassword;
